import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ScanLine, Camera, Layers, X, AlertTriangle, CheckCircle2 } from "lucide-react";
import { CONF } from "./constants";

export function ScanLabelCard({ scanning, fileRef, onFileChange, onBulk }) {
  return (
    <Card className="border-kilted-gold/40 bg-kilted-gold/5" data-testid="scan-label-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <ScanLine className="h-5 w-5 text-kilted-gold" /> Scan a label photo
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-sm text-muted-foreground">
          Snap or upload a photo of the parcel's shipping label and we'll read the sender, destination and estimate the size &amp; weight for you. Always review before confirming.
        </p>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFileChange} data-testid="scan-label-input" />
        <Button type="button" variant="outline" disabled={scanning} onClick={() => fileRef.current?.click()} data-testid="scan-label-button"
          className="w-full gap-2 border-kilted-gold text-kilted-green hover:bg-kilted-gold/10">
          {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
          {scanning ? "Reading label…" : "Upload / take a label photo"}
        </Button>
        <button type="button" onClick={onBulk} data-testid="bulk-scan-link" className="mt-2 flex w-full items-center justify-center gap-1.5 text-xs font-medium text-kilted-green hover:underline">
          <Layers className="h-3.5 w-3.5" /> Got a whole run? Scan multiple labels at once
        </button>
      </CardContent>
    </Card>
  );
}

export function ScanReviewBanner({ scanResult, onDismiss }) {
  if (!scanResult) return null;
  return (
    <Card className="border-l-4 border-l-kilted-gold" data-testid="scan-result-banner">
      <CardContent className="space-y-3 pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ScanLine className="h-4 w-4 text-kilted-green" />
            <span className="text-sm font-semibold text-foreground">Scan review</span>
            <Badge variant="outline" className={CONF[scanResult.confidence]?.cls} data-testid="scan-confidence-badge">
              {CONF[scanResult.confidence]?.l || "Unknown"}
            </Badge>
          </div>
          <button type="button" onClick={onDismiss} data-testid="scan-result-dismiss" className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        {scanResult.warnings?.length ? (
          <ul className="space-y-1.5" data-testid="scan-warnings">
            {scanResult.warnings.map((w) => (
              <li key={`warn-${w}`} className="flex items-start gap-2 text-xs text-amber-700">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {w}
              </li>
            ))}
          </ul>
        ) : (
          <p className="flex items-center gap-2 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Everything read cleanly — please still confirm before booking.</p>
        )}
      </CardContent>
    </Card>
  );
}
